/* eslint-disable indent */
/* eslint-disable no-nested-ternary */
import React, {
    useEffect,
    useState,
    useRef,
    useLayoutEffect,
    useCallback,
} from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import usePreviousRef from '../../../hooks/usePreviousRef';

const InputContainerWrapper = styled.div`
    position: relative;
`;

const InputWrapper = styled.input`
    border: none;
    border-bottom: 2px solid #d8d8d8;
    border-radius: 0;
    font-size: 24px;
    height: 45px;
    margin-right: 12px;
    text-align: center;
    width: 48px;
    -webkit-text-security: disc;
    font-family: text-security-disc;
`;

const ErrorWrapper = styled.span`
    color: #f00;
    display: block;
    font-size: 12px;
    line-height: 1.3;
    margin-top: 15px;
`;

const SingleOtpInput = props => {
    const { focus, autoFocus, ...rest } = props;
    const inputRef = useRef(null);
    const prevFocus = usePreviousRef(Boolean(focus));
    useLayoutEffect(() => {
        if (inputRef.current) {
            if (focus && autoFocus) {
                inputRef.current.focus();
            }
            if (focus && autoFocus && focus !== prevFocus) {
                inputRef.current.focus();
                inputRef.current.select();
            }
        }
    }, [autoFocus, focus, prevFocus]);

    return <InputWrapper ref={inputRef} {...rest} />;
};

const OTPInput = props => {
    const {
        id,
        name,
        placeholder,
        wrapperClass,
        serverError,
        input,
        autocomplete = 'off',
        disabled = false,
        meta,
        length,
        isNumberInput,
        autoFocus,
        handleChangeOtp,
        inputClassName,
        isReset,
        setReset,
        inputStyle,
        ...rest
    } = props;
    const [serverErr, setServerErr] = useState('');
    const [activeInput, setActiveInput] = useState(0);
    const [otpValues, setOTPValues] = useState(Array(length).fill(''));

    useEffect(() => {
        setServerErr(serverError);
    }, [serverError]);

    useEffect(() => {
        if (isReset) {
            setOTPValues(Array(length).fill(''));
            setReset(false);
            setActiveInput(0);
        }
    }, [isReset, length, setReset]);

    // Helper to return OTP from inputs
    const handleOtpChange = useCallback(
        otp => {
            const otpValue = otp.join('');
            const formFieldChangeMethod =
                input && input.onChange
                    ? input.onChange
                    : handleChangeOtp
                    ? handleChangeOtp
                    : null;
            setServerErr('');
            formFieldChangeMethod(otpValue);
        },
        [input, handleChangeOtp]
    );

    // Helper to return value with the right type: 'text' or 'number'
    const getRightValue = useCallback(
        str => {
            const changedValue = str;
            if (!isNumberInput) {
                return changedValue;
            }
            return !changedValue || /\d/.test(changedValue) ? changedValue : '';
        },
        [isNumberInput]
    );

    // Change OTP value at focussing input
    const changeCodeAtFocus = useCallback(
        str => {
            const updatedOTPValues = [...otpValues];
            updatedOTPValues[activeInput] = str[0] || '';
            setOTPValues(updatedOTPValues);
            handleOtpChange(updatedOTPValues);
        },
        [activeInput, handleOtpChange, otpValues]
    );

    // Focus `inputIndex` input
    const focusInput = useCallback(
        inputIndex => {
            const selectedIndex = Math.max(Math.min(length - 1, inputIndex), 0);
            setActiveInput(selectedIndex);
        },
        [length]
    );
    const focusPrevInput = useCallback(() => {
        focusInput(activeInput - 1);
    }, [activeInput, focusInput]);
    const focusNextInput = useCallback(() => {
        focusInput(activeInput + 1);
    }, [activeInput, focusInput]);

    // Handle onFocus input
    const handleOnFocus = useCallback(
        index => () => {
            focusInput(index);
        },
        [focusInput]
    );

    // Handle onChange value for each input
    const handleOnChange = useCallback(
        event => {
            const val = getRightValue(event.currentTarget.value);
            if (!val) {
                event.preventDefault();
                return;
            }
            changeCodeAtFocus(val);
            focusNextInput();
        },
        [changeCodeAtFocus, focusNextInput, getRightValue]
    );

    // Handle onBlur input
    const onBlur = useCallback(() => {
        setActiveInput(-1);
    }, []);

    // Handle onKeyDown input
    const handleOnKeyDown = useCallback(
        event => {
            switch (event.key) {
                case 'Backspace':
                case 'Delete': {
                    event.preventDefault();
                    if (otpValues[activeInput]) {
                        changeCodeAtFocus('');
                    } else {
                        focusPrevInput();
                    }
                    break;
                }
                case 'ArrowLeft': {
                    event.preventDefault();
                    focusPrevInput();
                    break;
                }
                case 'ArrowRight': {
                    event.preventDefault();
                    focusNextInput();
                    break;
                }
                case ' ': {
                    event.preventDefault();
                    break;
                }
                default:
                    break;
            }
        },
        [
            activeInput,
            changeCodeAtFocus,
            focusNextInput,
            focusPrevInput,
            otpValues,
        ]
    );
    const handleOnPaste = useCallback(
        event => {
            event.preventDefault();
            const pastedData = event.clipboardData
                .getData('text/plain')
                .trim()
                .slice(0, length - activeInput)
                .split('');
            if (pastedData) {
                let nextFocusIndex = 0;
                const updatedOTPValues = [...otpValues];
                updatedOTPValues.forEach((val, index) => {
                    if (index >= activeInput) {
                        const changedValue = getRightValue(
                            pastedData.shift() || val
                        );
                        if (changedValue) {
                            updatedOTPValues[index] = changedValue;
                            nextFocusIndex = index;
                        }
                    }
                });
                setOTPValues(updatedOTPValues);
                setActiveInput(Math.min(nextFocusIndex + 1, length - 1));
            }
        },
        [activeInput, getRightValue, length, otpValues]
    );

    return (
        <InputContainerWrapper
            className={`input-container ${wrapperClass || ''}${
                serverErr || (meta && meta.error && meta.touched)
                    ? ' error'
                    : ''
            }`}
        >
            {Array(length)
                .fill('')
                .map((item, index) => (
                    <SingleOtpInput
                        type="text"
                        id={`${id}_${index}`}
                        key={`single-input-${index}`}
                        aria-label={`single-input-${index}`}
                        name={name}
                        placeholder={placeholder}
                        wrapperClass={wrapperClass}
                        serverError={serverErr}
                        input={input}
                        autocomplete={autocomplete}
                        disabled={disabled}
                        meta={meta}
                        length={length}
                        isNumberInput={isNumberInput}
                        autoFocus={autoFocus}
                        handleChangeOtp={handleChangeOtp}
                        inputClassName={inputClassName}
                        inputStyle={inputStyle}
                        focus={activeInput === index}
                        value={otpValues && otpValues[index]}
                        onFocus={handleOnFocus(index)}
                        onChange={handleOnChange}
                        onKeyDown={handleOnKeyDown}
                        onBlur={onBlur}
                        onPaste={handleOnPaste}
                        style={inputStyle}
                        className={inputClassName}
                        {...rest}
                    />
                ))}
            {serverErr && (
                <ErrorWrapper className="error-message">
                    {serverErr}
                </ErrorWrapper>
            )}
            {!serverErr && meta && meta.touched && meta.error && (
                <ErrorWrapper className="error-message">
                    {meta.error}
                </ErrorWrapper>
            )}
        </InputContainerWrapper>
    );
};

OTPInput.propTypes = {
    className: PropTypes.string,
    defaultSelected: PropTypes.string,
    handleChange: PropTypes.func,
    labelField: PropTypes.string,
    options: PropTypes.array,
    valueField: PropTypes.string,
    wrapperClass: PropTypes.string,
    id: PropTypes.string,
    name: PropTypes.string,
    placeholder: PropTypes.string,
    serverError: PropTypes.string,
    input: PropTypes.object,
    autocomplete: PropTypes.string,
    disabled: PropTypes.bool,
    meta: PropTypes.object,
    length: PropTypes.number,
    isNumberInput: PropTypes.bool,
    autoFocus: PropTypes.bool,
    handleChangeOtp: PropTypes.func,
    inputClassName: PropTypes.string,
    inputStyle: PropTypes.string,
    isReset: PropTypes.bool,
    setReset: PropTypes.func,
};

SingleOtpInput.propTypes = {
    focus: PropTypes.bool,
    autoFocus: PropTypes.bool,
};

export default OTPInput;
